import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { ImageStoreService } from '@imagise/generated-imagise-cloud-api-client';
import { FormatInfo } from '../../domain/interfaces/model-options';
import { FileResult } from '@imagise/entities';
import { BASE_URL_PROVIDER, ObjectUrlHolder, joinUrl } from '@imagise/shared';
import { checkOperationResponse } from '../../../error-reporting/operation-request';

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  #imageStoreService = inject(ImageStoreService);
  private readonly apiBasePath = inject(BASE_URL_PROVIDER);

  private readonly downloadUrl = new ObjectUrlHolder();

  outputFormat?: FormatInfo;

  requestDownloadLink(file: FileResult): Observable<FileResult> {
    if (!file) {
      throw new Error('Empty file info is not allowed');
    }

    if (file.DownloadUrl || file.UploadFileName) {
      return of(file);
    }

    if (file.FileBlob) {
      const downloadUrl = this.downloadUrl.create(file.FileBlob);
      return of({
        ...file,
        UploadFileName: downloadUrl,
        DownloadUrl: downloadUrl,
      });
    }

    if (!file.id) {
      throw new Error('Cannot build download link without file id');
    }

    return this.#imageStoreService.download(encodeURIComponent(file.id)).pipe(
      checkOperationResponse(),
      map(resultBlob => {
        const downloadUrl = this.downloadUrl.create(resultBlob);
        return {
          ...file,
          UploadFileName: downloadUrl,
          DownloadUrl: downloadUrl,
          FileBlob: resultBlob,
          FileLength: resultBlob.size,
        };
      })
    );
  }

  getDownloadLink(file: FileResult): string {
    if (!file) {
      throw new Error('Empty file info is not allowed');
    }

    if (file.DownloadUrl) {
      return file.DownloadUrl;
    }

    if (file.UploadFileName) {
      return file.UploadFileName;
    }

    if (!file.id) {
      throw new Error('Cannot build download link without file id');
    }

    return this.buildDownloadPath(file.id);
  }

  private buildDownloadPath(fileId: string): string {
    return joinUrl(this.apiBasePath, `download/${encodeURIComponent(fileId)}`);
  }
}
